import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  fetchTasksMe,
  isTasksLeaderRole,
  RM_TASKS_ENABLED,
  tasksApi,
} from '../../../Tasks/tasksApi';
import {
  Card,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  Select,
  Table,
  TextInput,
  Toolbar,
} from '../../../ui';
import type { BadgeTone } from '../../../ui';
import { Segmented, StatTile, Swatch } from './ui-pending';
import type { StatTone } from './ui-pending';
import './TeamPerformancePage.scss';

/**
 * Team Performance — nineteen RMs and growing, read side by side.
 *
 * The RM dashboard's team table answered this until the team outgrew it:
 * nineteen rows of ten numbers is a thing you read one agent at a time,
 * which is the opposite of comparing them. Three charts do the comparing,
 * and the table underneath keeps the exact figures for when the chart has
 * told you where to look.
 *
 * Everything comes from one call. The action-SLA ranking is the reason that
 * endpoint exists — per-agent SLA used to mean a request per RM.
 */

interface SlaRatio {
  met: number;
  total: number;
  pct: number | null;
}

interface AgentRow {
  externalId: string;
  name: string;
  photoUrl: string | null;
  podKeys: string[];
  students: number;
  openTasks: number;
  overdue: number;
  dueToday: number;
  pastResolution: number;
  closed: number;
  sla: SlaRatio;
  resolution: SlaRatio;
}

interface PerformanceDay {
  day: string;
  label: string;
  total: number;
  byPod: Record<string, number>;
}

interface Performance {
  window: { from: string; to: string; timeZone: string };
  series: PerformanceDay[];
  pods: { key: string; name: string }[];
  podKey: string | null;
  agents: AgentRow[];
  team: Omit<AgentRow, 'externalId' | 'name' | 'photoUrl' | 'podKeys'> & {
    medianStudents: number;
    medianClosed: number;
  };
  empty: boolean;
}

const iso = (d: Date): string => d.toISOString().slice(0, 10);
const today = (): string => iso(new Date());
const daysAgo = (n: number): string => iso(new Date(Date.now() - n * 86_400_000));
const monthStart = (): string => `${today().slice(0, 7)}-01`;

type Preset = 'mtd' | '7d' | '30d' | 'custom';

/**
 * A percentage drawn from very few tickets is not a measurement. "0% (0/1)"
 * reads as a catastrophe and means one ticket; the bar is drawn muted and
 * the ratio always shown, so nobody is ranked on a single data point.
 */
const THIN = 3;

type PctTone = 'muted' | 'success' | 'warning' | 'danger';

const pctTone = (pct: number | null, total: number): PctTone => {
  if (pct === null || total < THIN) return 'muted';
  if (pct >= 85) return 'success';
  if (pct >= 60) return 'warning';
  return 'danger';
};

/** StatTile has no "muted"; a thin or empty ratio reads as plain neutral there. */
const statTone = (t: PctTone): StatTone => (t === 'muted' ? 'neutral' : t);

/** One RM's row in a ranked horizontal bar chart. */
const RankedBar: React.FC<{
  label: string;
  sub?: string;
  pct: number | null;
  total: number;
  value: string;
  onClick?: () => void;
}> = ({ label, sub, pct, total, value, onClick }) => {
  const tone = pctTone(pct, total);
  return (
    <button
      type="button"
      className="tpf-hbar"
      onClick={onClick}
      disabled={!onClick}
      title={onClick ? `Open ${label}'s tickets` : undefined}
    >
      <span className="tpf-hbar__label">
        <span className="tpf-hbar__name">{label}</span>
        {sub && <span className="tpf-hbar__sub">{sub}</span>}
      </span>
      <span className="tpf-hbar__track">
        {/* Bar length is the data itself, so its width stays inline. */}
        <span
          className={`tpf-hbar__fill tpf-tone--${tone}`}
          style={{ width: `${Math.max(pct ?? 0, pct === null ? 0 : 1.5)}%` }}
        />
      </span>
      <span className={`tpf-hbar__value tpf-text--${tone}`}>{value}</span>
    </button>
  );
};

/** Stacked bar: what is actually sitting on this RM's pile right now. */
const LoadBar: React.FC<{
  row: AgentRow;
  max: number;
  onClick?: () => void;
}> = ({ row, max, onClick }) => {
  const onTrack = Math.max(0, row.openTasks - row.overdue - row.dueToday);
  const seg = (n: number, tone: PctTone, title: string) =>
    n > 0 ? (
      <span className={`tpf-hbar__seg tpf-tone--${tone}`} style={{ width: `${(n / max) * 100}%` }} title={title} />
    ) : null;
  return (
    <button type="button" className="tpf-hbar" onClick={onClick} disabled={!onClick}>
      <span className="tpf-hbar__label">
        <span className="tpf-hbar__name">{row.name}</span>
      </span>
      <span className="tpf-hbar__track tpf-hbar__track--stack">
        {seg(row.overdue, 'danger', `${row.overdue} overdue`)}
        {seg(row.dueToday, 'warning', `${row.dueToday} due today`)}
        {seg(onTrack, 'muted', `${onTrack} on track`)}
      </span>
      <span className="tpf-hbar__value">
        {row.overdue > 0 && <span className="tpf-text--danger">{row.overdue} late</span>}
        {row.overdue > 0 && <span className="ds-text-muted"> · </span>}
        <span className="ds-text-muted tpf-normal">{row.openTasks} open</span>
      </span>
    </button>
  );
};

/**
 * One colour per desk, so a bar and a band in the trend mean the same RM group.
 * Desks are a fixed, known set, so each gets a design-system tone (fill via
 * the .tpf-fill--<tone> classes) rather than a hex value.
 */
const POD_TONE: Record<string, BadgeTone> = {
  US_CANADA: 'primary',
  IND_UAE: 'accent',
  IND_UK: 'success',
  IND_REST: 'warning',
};
const podTone = (key?: string): BadgeTone => POD_TONE[key ?? ''] ?? 'neutral';

/** Rounded axis top that leaves headroom, so the tallest bar isn't flush. */
const niceMax = (v: number): number => {
  if (v <= 5) return 5;
  const step = Math.pow(10, Math.floor(Math.log10(v))) / 2;
  return Math.ceil((v * 1.1) / step) * step;
};

/**
 * Tickets closed per RM — columns against a real axis, tallest first,
 * coloured by desk.
 *
 * This replaced a caseload-against-closures scatter. The scatter assumed
 * caseloads differ enough to spread along an axis; they don't, because
 * every RM was given an even share of learners, so all nineteen dots
 * stacked into one vertical line. Closures are where the spread actually
 * is, and the desk colouring carries the comparison the scatter was
 * reaching for.
 */
const ClosuresByAgent: React.FC<{
  rows: AgentRow[];
  onPick: (externalId: string) => void;
}> = ({ rows, onPick }) => {
  const sorted = [...rows].sort((a, b) => b.closed - a.closed);
  const W = 900;
  const H = 300;
  const PAD = { l: 42, r: 12, t: 12, b: 64 };
  const max = niceMax(Math.max(1, ...sorted.map((r) => r.closed)));
  const plotW = W - PAD.l - PAD.r;
  const plotH = H - PAD.t - PAD.b;
  const slot = plotW / Math.max(1, sorted.length);
  const barW = Math.min(34, slot * 0.62);
  const y = (v: number) => PAD.t + plotH - (v / max) * plotH;
  const ticks = [0, max / 4, max / 2, (max * 3) / 4, max];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="tpf-svg" role="img" aria-label="Tickets closed by RM">
      {ticks.map((t) => (
        <g key={t}>
          <line x1={PAD.l} y1={y(t)} x2={W - PAD.r} y2={y(t)} className="tpf-svg__grid" />
          <text x={PAD.l - 7} y={y(t) + 3} className="tpf-svg__tick" fontSize="10" textAnchor="end">
            {Math.round(t)}
          </text>
        </g>
      ))}
      {sorted.map((r, i) => {
        const cx = PAD.l + slot * i + slot / 2;
        const h = Math.max(r.closed > 0 ? 2 : 0, plotH - (y(r.closed) - PAD.t));
        return (
          <g key={r.externalId} className="tpf-svg__click" onClick={() => onPick(r.externalId)}>
            <rect
              x={cx - barW / 2} y={y(r.closed)} width={barW} height={h}
              rx={2} className={`tpf-fill--${podTone(r.podKeys[0])}`}
            >
              <title>{`${r.name} — ${r.closed} closed`}</title>
            </rect>
            {r.closed > 0 && (
              <text x={cx} y={y(r.closed) - 4} className="tpf-svg__value" fontSize="10" textAnchor="middle">
                {r.closed}
              </text>
            )}
            <text
              x={cx} y={PAD.t + plotH + 12} className="tpf-svg__label" fontSize="10"
              textAnchor="end" transform={`rotate(-40 ${cx} ${PAD.t + plotH + 12})`}
            >
              {r.name.split(' ')[0]}
            </text>
          </g>
        );
      })}
      <line x1={PAD.l} y1={PAD.t + plotH} x2={W - PAD.r} y2={PAD.t + plotH} className="tpf-svg__axis" />
    </svg>
  );
};

/** Closures per day, stacked by desk — is the team getting through more? */
const ClosureTrend: React.FC<{
  series: { day: string; label: string; total: number; byPod: Record<string, number> }[];
  pods: { key: string; name: string }[];
}> = ({ series, pods }) => {
  const W = 900;
  const H = 260;
  const PAD = { l: 42, r: 12, t: 12, b: 42 };
  const max = niceMax(Math.max(1, ...series.map((d) => d.total)));
  const plotW = W - PAD.l - PAD.r;
  const plotH = H - PAD.t - PAD.b;
  const slot = plotW / Math.max(1, series.length);
  const barW = Math.min(26, slot * 0.68);
  const y = (v: number) => PAD.t + plotH - (v / max) * plotH;
  const ticks = [0, max / 2, max];
  const keys = pods.map((p) => p.key);
  // A long window would print a label per day on top of its neighbour.
  const every = series.length > 20 ? Math.ceil(series.length / 12) : 1;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="tpf-svg" role="img" aria-label="Closures per day by desk">
      {ticks.map((t) => (
        <g key={t}>
          <line x1={PAD.l} y1={y(t)} x2={W - PAD.r} y2={y(t)} className="tpf-svg__grid" />
          <text x={PAD.l - 7} y={y(t) + 3} className="tpf-svg__tick" fontSize="10" textAnchor="end">
            {Math.round(t)}
          </text>
        </g>
      ))}
      {series.map((d, i) => {
        const cx = PAD.l + slot * i + slot / 2;
        let acc = 0;
        return (
          <g key={d.day}>
            {keys.map((k) => {
              const v = d.byPod[k] ?? 0;
              if (v <= 0) return null;
              const top = y(acc + v);
              const h = y(acc) - top;
              acc += v;
              return (
                <rect key={k} x={cx - barW / 2} y={top} width={barW} height={h} className={`tpf-fill--${podTone(k)}`}>
                  <title>{`${d.label} — ${v} closed on ${k}`}</title>
                </rect>
              );
            })}
            {i % every === 0 && (
              <text x={cx} y={PAD.t + plotH + 14} className="tpf-svg__label" fontSize="9" textAnchor="middle">
                {d.label}
              </text>
            )}
          </g>
        );
      })}
      <line x1={PAD.l} y1={PAD.t + plotH} x2={W - PAD.r} y2={PAD.t + plotH} className="tpf-svg__axis" />
    </svg>
  );
};

/** Shared key for the two desk-coloured charts. */
const PodLegend: React.FC<{ pods: { key: string; name: string }[] }> = ({ pods }) => (
  <div className="tpf-legend">
    {pods.map((p) => (
      <span key={p.key} className="tpf-legend__item">
        <Swatch tone={podTone(p.key)} /> {p.name}
      </span>
    ))}
  </div>
);

const PERIOD_OPTIONS: { value: Preset; label: string }[] = [
  { value: 'mtd', label: 'This month' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom' },
];

const SORT_OPTIONS = [
  { value: 'sla', label: 'Sort: worst SLA' },
  { value: 'overdue', label: 'Sort: most overdue' },
  { value: 'closed', label: 'Sort: most closed' },
  { value: 'name', label: 'Sort: name' },
];

const TeamPerformancePage: React.FC = () => {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [data, setData] = useState<Performance | null>(null);
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState<Preset>('mtd');
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [podKey, setPodKey] = useState('');
  const [sortKey, setSortKey] = useState<'sla' | 'overdue' | 'closed' | 'name'>('sla');

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'mtd') { setFrom(monthStart()); setTo(today()); }
    else if (p === '7d') { setFrom(daysAgo(6)); setTo(today()); }
    else if (p === '30d') { setFrom(daysAgo(29)); setTo(today()); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ from, to, tz: 'Asia/Kolkata' });
      if (podKey) p.set('podKey', podKey);
      setData(await tasksApi<Performance>(`/v1/team-overview/performance?${p.toString()}`));
    } catch (err: any) {
      toast.error(err?.message || 'Could not load team performance');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, podKey]);

  useEffect(() => {
    fetchTasksMe().then((m) => setAllowed(isTasksLeaderRole(m?.role)));
  }, []);

  useEffect(() => {
    if (allowed) void load();
  }, [allowed, load]);

  // The queue reads its filters from the query string, and spells the agent
  // one `agent` — so a click here lands on that RM's tickets already filtered.
  const openTickets = (externalId: string) =>
    navigate(`${process.env.PUBLIC_URL}/pages/rm-tasks?agent=${encodeURIComponent(externalId)}`);

  const bySla = useMemo(
    () =>
      [...(data?.agents ?? [])].sort((a, b) => {
        // Worst first, but a thin denominator is not "worst" — it is "unknown",
        // so those sink to the bottom rather than topping a list of problems.
        const av = a.sla.total < THIN ? 999 : a.sla.pct ?? 999;
        const bv = b.sla.total < THIN ? 999 : b.sla.pct ?? 999;
        return av - bv;
      }),
    [data],
  );
  const byLoad = useMemo(
    () => [...(data?.agents ?? [])].sort((a, b) => b.overdue - a.overdue || b.openTasks - a.openTasks),
    [data],
  );
  const tableRows = useMemo(() => {
    const rows = [...(data?.agents ?? [])];
    rows.sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name);
      if (sortKey === 'sla') return (a.sla.pct ?? 101) - (b.sla.pct ?? 101);
      return (b[sortKey] as number) - (a[sortKey] as number);
    });
    return rows;
  }, [data, sortKey]);
  const maxLoad = Math.max(1, ...(data?.agents ?? []).map((a) => a.openTasks));

  const header = (subtitle: React.ReactNode) => (
    <PageHeader eyebrow="RM Tasks" title="Team Performance" subtitle={subtitle} />
  );

  if (!RM_TASKS_ENABLED) {
    return (
      <Page>
        {header(undefined)}
        <EmptyState title="Not enabled" description="This feature isn't enabled in this environment yet." />
      </Page>
    );
  }
  if (allowed === null) {
    return (
      <Page>
        {header(undefined)}
        <Loader />
      </Page>
    );
  }
  if (!allowed) {
    return (
      <Page>
        {header(undefined)}
        <EmptyState
          title="Leaders only"
          description="Team performance is for team leads and admins — it reads one RM's numbers beside another's."
        />
      </Page>
    );
  }

  const t = data?.team;
  const scope = data?.podKey
    ? data.pods.find((p) => p.key === data.podKey)?.name ?? data.podKey
    : 'the team';

  return (
    <Page>
      {header(<>How {scope} is doing — who is behind, who is carrying what, and who is closing it.</>)}

      <Toolbar>
        {/* Segmented is a button group, so its label is a caption plus aria-label, not a <label>. */}
        <div className="ds-field">
          <span className="ds-field__label" aria-hidden>Period</span>
          <Segmented
            ariaLabel="Period"
            size="sm"
            options={PERIOD_OPTIONS}
            value={preset}
            onChange={applyPreset}
          />
        </div>
        {preset === 'custom' && (
          <>
            <Field label="From" className="tpf-date">
              {(f) => (
                <TextInput {...f} type="date" size="sm" value={from} onChange={(e) => setFrom(e.target.value)} />
              )}
            </Field>
            <Field label="To" className="tpf-date">
              {(f) => (
                <TextInput {...f} type="date" size="sm" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
              )}
            </Field>
          </>
        )}
        <Field label="Desk" className="tpf-desk">
          {(f) => (
            <Select
              {...f}
              size="sm"
              value={podKey}
              options={[
                { value: '', label: 'All desks' },
                ...(data?.pods ?? []).map((p) => ({ value: p.key, label: p.name })),
              ]}
              onChange={(e) => setPodKey(e.target.value)}
            />
          )}
        </Field>
      </Toolbar>

      {loading && !data && <Loader label="Loading team performance…" />}

      {data && (
        <>
          <div className="rm-stats">
            <StatTile
              label="Action SLA" hint={`${t!.sla.met} of ${t!.sla.total} actioned in time`}
              value={t!.sla.pct === null ? '—' : `${t!.sla.pct}%`}
              tone={statTone(pctTone(t!.sla.pct, t!.sla.total))}
            />
            <StatTile
              label="Resolution SLA" hint={`${t!.resolution.met} of ${t!.resolution.total} closed in time`}
              value={t!.resolution.pct === null ? '—' : `${t!.resolution.pct}%`}
              tone={statTone(pctTone(t!.resolution.pct, t!.resolution.total))}
            />
            <StatTile
              label="Overdue now" hint="untouched past the action deadline"
              value={String(t!.overdue)}
              tone={t!.overdue > 0 ? 'danger' : 'neutral'}
            />
            <StatTile label="Closed" hint={`${from} to ${to}`} value={String(t!.closed)} />
            <StatTile label="Open" hint={`${t!.dueToday} due today`} value={String(t!.openTasks)} />
          </div>

          <Card
            title="Who is hitting the action SLA"
            subtitle={
              <>
                Share of tickets each RM touched before the deadline, worst first.
                Grey means too few tickets to judge — the ratio is always shown.
                {t!.sla.pct !== null && ` ${scope === 'the team' ? 'Team' : scope} average is ${t!.sla.pct}%.`}
              </>
            }
          >
            <div className="tpf-bars">
              {bySla.map((a) => (
                <RankedBar
                  key={a.externalId}
                  label={a.name}
                  sub={a.podKeys.join(', ')}
                  pct={a.sla.pct}
                  total={a.sla.total}
                  value={a.sla.pct === null ? 'no tickets due' : `${a.sla.pct}% (${a.sla.met}/${a.sla.total})`}
                  onClick={() => openTickets(a.externalId)}
                />
              ))}
            </div>
          </Card>

          <Card
            title="What is on each pile right now"
            subtitle={
              <span className="tpf-legend">
                <span className="tpf-legend__item"><Swatch tone="danger" /> overdue</span>
                <span className="tpf-legend__item"><Swatch tone="warning" /> due today</span>
                <span className="tpf-legend__item"><Swatch tone="neutral" /> on track.</span>
                <span>Current state, not the period.</span>
              </span>
            }
          >
            <div className="tpf-bars">
              {byLoad.map((a) => (
                <LoadBar key={a.externalId} row={a} max={maxLoad} onClick={() => openTickets(a.externalId)} />
              ))}
            </div>
          </Card>

          <Card
            title="Tickets closed, by RM"
            subtitle={
              <>
                Throughput over {from} to {to}, tallest first. Caseload is near identical
                across the team — {t!.medianStudents} learners each — so this, not the
                size of the list, is where RMs actually differ.
              </>
            }
            actions={<PodLegend pods={data.pods} />}
          >
            <ClosuresByAgent rows={data.agents} onPick={openTickets} />
          </Card>

          <Card
            title="Closures per day"
            subtitle="Stacked by desk, so a desk that is ramping shows as a growing band rather than a single number at the end of the month."
            actions={<PodLegend pods={data.pods} />}
          >
            <ClosureTrend series={data.series} pods={data.podKey
              ? data.pods.filter((p) => p.key === data.podKey)
              : data.pods} />
          </Card>

          <Card
            flush
            title="Every number"
            subtitle="Learners and open counts are today; closed and both SLAs are the period."
            actions={
              <Select
                size="sm"
                aria-label="Sort rows"
                className="tpf-sort"
                value={sortKey}
                options={SORT_OPTIONS}
                onChange={(e) => setSortKey(e.target.value as typeof sortKey)}
              />
            }
          >
            <Table caption="Team performance by RM">
              <thead>
                <tr>
                  <th>RM</th>
                  <th>Desk</th>
                  <th className="ds-num">Learners</th>
                  <th className="ds-num">Open</th>
                  <th className="ds-num">Due today</th>
                  <th className="ds-num">Overdue</th>
                  <th className="ds-num">Past resolution</th>
                  <th className="ds-num">Closed</th>
                  <th className="ds-num">Action SLA</th>
                  <th className="ds-num">Resolution SLA</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((a) => (
                  <tr key={a.externalId} className="tpf-row" onClick={() => openTickets(a.externalId)}>
                    <td className="ds-strong">{a.name}</td>
                    <td className="rm-caption">{a.podKeys.join(', ') || '—'}</td>
                    <td className="ds-num">{a.students}</td>
                    <td className="ds-num">{a.openTasks}</td>
                    <td className="ds-num">{a.dueToday || ''}</td>
                    <td className={`ds-num${a.overdue ? ' tpf-text--danger' : ''}`}>
                      {a.overdue || ''}
                    </td>
                    <td className="ds-num">{a.pastResolution || ''}</td>
                    <td className="ds-num">{a.closed}</td>
                    <td className={`ds-num tpf-text--${pctTone(a.sla.pct, a.sla.total)}`}>
                      {a.sla.pct === null ? '—' : `${a.sla.pct}%`}
                      <span className="rm-caption"> ({a.sla.met}/{a.sla.total})</span>
                    </td>
                    <td className={`ds-num tpf-text--${pctTone(a.resolution.pct, a.resolution.total)}`}>
                      {a.resolution.pct === null ? '—' : `${a.resolution.pct}%`}
                      <span className="rm-caption"> ({a.resolution.met}/{a.resolution.total})</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}
    </Page>
  );
};

export default TeamPerformancePage;
